import { useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { studentService } from '@/services/studentService'
import { CreatorType } from '@/types/practice-test'
import {
  StudentListTestResponse,
  StudentStatisticsInClassResponse,
} from '@/types/student'
import StudentsDetail from '@/features/students/detail'

export const Route = createFileRoute(
  '/_authenticated/students/detail/$studentId/$classId'
)({
  component: DetailStudent,
})

function DetailStudent() {
  const { studentId, classId } = Route.useParams()
  const [page, setPage] = useState(1)
  const [limit] = useState(10)
  const [creatorType, setCreatorType] = useState<CreatorType | null>(null)
  const [isPractice, setIsPractice] = useState<boolean | null>(true)
  const [search, setSearch] = useState<string>('')

  const {
    data: studentListTest,
    isLoading,
    error,
  } = useQuery<StudentListTestResponse>({
    queryKey: [
      'student',
      studentId,
      classId,
      page,
      limit,
      creatorType,
      isPractice,
      search,
    ],
    queryFn: () =>
      studentService.getStudentListTest({
        student_id: Number(studentId),
        class_id: Number(classId),
        page,
        limit,
        creator_type: creatorType ?? undefined,
        is_practice: isPractice ?? undefined,
        search: search || undefined,
      }),
    enabled: !!studentId && !!classId,
    placeholderData: keepPreviousData,
  })

  const {
    data: studentStatisticsInClass,
    isLoading: isLoadingStudentStatisticsInClass,
    error: errorStudentStatisticsInClass,
  } = useQuery<StudentStatisticsInClassResponse>({
    queryKey: ['studentStatisticsInClass', studentId, classId],
    queryFn: () =>
      studentService.getStudentStatisticsInClass({
        student_id: Number(studentId),
        class_id: Number(classId),
      }),
    enabled: !!studentId && !!classId,
  })

  if (error || errorStudentStatisticsInClass) {
    return <div className='p-4 text-red-600'>Error loading student.</div>
  }

  if (!studentListTest || !studentStatisticsInClass) {
    return null
  }

  return (
    <div className='py-4'>
      <StudentsDetail
        studentId={Number(studentId)}
        classId={Number(classId)}
        studentListTest={studentListTest}
        studentStatisticsInClass={studentStatisticsInClass}
        currentPage={page}
        onPageChange={setPage}
        creatorType={creatorType}
        isLoading={isLoading}
        isLoadingStudentStatisticsInClass={isLoadingStudentStatisticsInClass}
        onCreatorTypeChange={setCreatorType}
        isPractice={isPractice}
        onIsPracticeChange={setIsPractice}
        search={search}
        onSearchChange={setSearch}
      />
    </div>
  )
}
